import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import control from "../../assets/control.png";
import Logo2 from "../../assets/Edolutions 1.png";
import {
  FaGraduationCap,
  FaHome,
  FaChild,
} from "react-icons/fa";
import {
  MdKeyboardDoubleArrowRight,
  MdSearch,
  MdOutlineMenuBook,
  MdTranslate,
} from "react-icons/md";
import {
  PiChalkboardTeacherFill,
  PiStudentFill,
  PiUserCircleGearFill,
} from "react-icons/pi";
import { SiGoogleclassroom } from "react-icons/si";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const { t } = useTranslation();
  const [showProfessions, setShowProfessions] = useState(false);

  const handleNavClick = () => toggleSidebar();

  const baseItem =
    "relative flex items-center gap-3 px-4 py-4 rounded-xl transition-colors";
  const baseIcon = "w-[22.5px] h-[22.5px]";
  const activeItem =
    "text-[#2A3A54] bg-white shadow-sm border border-black/10";
  const inactiveItem =
    "text-[#232738] hover:text-[#2A3A54] hover:bg-white/70";

  return (
    <>
      {/* Overlay for mobile */}
      <div
        onClick={toggleSidebar}
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-30 xl:hidden transition-opacity ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />

      <aside
        aria-label={t("adminDash.sidebar")}
        className={`${
          isOpen ? "w-[240px]" : "w-0"
        } fixed xl:relative left-0 top-0 h-screen z-40 xl:z-10 bg-white/90 backdrop-blur border-r border-black/10 transition-all duration-300 overflow-hidden`}
      >
        {/* Toggle button (mobile only) */}
        <img
          src={control}
          alt={t("adminDash.toggleSidebar")}
          className={`absolute block xl:hidden cursor-pointer -right-3 top-16 sm:top-16 md:top-5 w-7 border-2 border-black/10 rounded-full bg-white ${
            !isOpen && "rotate-180"
          }`}
          onClick={toggleSidebar}
        />

        {/* Brand section */}
        <div className="flex flex-col">
          <div className="w-full p-4 md:hidden block">
            <img src={Logo2} alt="Edolutions" className="h-9 w-auto" />
          </div>

          {/* Gradient strip */}
          <div
            className={`${
              isOpen ? "opacity-100" : "opacity-0"
            } h-1 w-full bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] transition-opacity`}
          />

          {/* Optional Search (mobile) */}
          <div className={`${isOpen ? "block" : "hidden"} px-4 py-3`}>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#232738]">
                <MdSearch className="sm:hidden block" />
              </span>
              <input
                type="text"
                placeholder={t("adminDash.search")}
                className="w-full pl-10 pr-3 py-2 rounded-lg border border-black/10 bg-white/80 shadow-sm outline-none sm:hidden block"
              />
            </div>
          </div>
        </div>

        {/* Nav links */}
        <nav className={`${isOpen ? "opacity-100" : "opacity-0"} transition-opacity`}>
          <ul className="mt-2 text-[14px] font-medium px-3 pb-6 space-y-1">

            <li>
              <NavLink
                to="./home"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  [baseItem, isActive ? activeItem : inactiveItem, "group"].join(" ")
                }
              >
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full opacity-0 group-[.active]:opacity-100 bg-gradient-to-b from-[#EE5F2B] to-[#2A3A54]" />
                <FaHome className={baseIcon} />
                <span>{t("adminDash.nav.home")}</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="./sessions"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  [baseItem, isActive ? activeItem : inactiveItem, "group"].join(" ")
                }
              >
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full opacity-0 group-[.active]:opacity-100 bg-gradient-to-b from-[#EE5F2B] to-[#2A3A54]" />
                <SiGoogleclassroom className={baseIcon} />
                <span>{t("adminDash.nav.onlineSessions")}</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="./mentor"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  [baseItem, isActive ? activeItem : inactiveItem, "group"].join(" ")
                }
              >
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full opacity-0 group-[.active]:opacity-100 bg-gradient-to-b from-[#EE5F2B] to-[#2A3A54]" />
                <PiChalkboardTeacherFill className={baseIcon} />
                <span>{t("adminDash.nav.mentors")}</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="./mentee"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  [baseItem, isActive ? activeItem : inactiveItem, "group"].join(" ")
                }
              >
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full opacity-0 group-[.active]:opacity-100 bg-gradient-to-b from-[#EE5F2B] to-[#2A3A54]" />
                <PiStudentFill className={baseIcon} />
                <span>{t("adminDash.nav.mentees")}</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="./kids"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  [baseItem, isActive ? activeItem : inactiveItem, "group"].join(" ")
                }
              >
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full opacity-0 group-[.active]:opacity-100 bg-gradient-to-b from-[#EE5F2B] to-[#2A3A54]" />
                <FaChild className={baseIcon} />
                <span>{t("adminDash.nav.kidsZone")}</span>
              </NavLink>
            </li>

            {/* Professions Zone dropdown */}
            <li>
              <div
                onClick={() => setShowProfessions(!showProfessions)}
                className={`${baseItem} cursor-pointer select-none ${inactiveItem}`}
              >
                <FaGraduationCap className={baseIcon} />
                <span className="flex-1">{t("adminDash.nav.professionsZone")}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`transition-transform ${showProfessions ? "rotate-90" : ""}`}
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </div>

              <ul
                className={`pl-8 mt-1 space-y-1 overflow-hidden transition-all duration-300 ${
                  showProfessions ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                {[
                  { to: "./add-profession", label: t("adminDash.nav.professions") },
                  { to: "./specialization", label: t("adminDash.nav.specializations") },
                  { to: "./ac-skill", label: t("adminDash.nav.skills") },
                  { to: "./ac-language", label: t("adminDash.nav.languages") },
                  { to: "./durations", label: t("adminDash.nav.sessionDurations") },
                ].map((item, i) => (
                  <li key={i}>
                    <NavLink
                      to={item.to}
                      onClick={handleNavClick}
                      className={({ isActive }) =>
                        [
                          "flex items-center gap-2 py-2 px-2 rounded-lg text-sm transition-colors",
                          isActive
                            ? "text-[#2A3A54] bg-white shadow border border-black/10"
                            : "text-[#232738] hover:text-[#2A3A54] hover:bg-white/70",
                        ].join(" ")
                      }
                    >
                      <MdKeyboardDoubleArrowRight className="w-[18px] h-[18px]" />
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </li>

            <li>
              <NavLink
                to="./knowledge"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  [baseItem, isActive ? activeItem : inactiveItem, "group"].join(" ")
                }
              >
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full opacity-0 group-[.active]:opacity-100 bg-gradient-to-b from-[#EE5F2B] to-[#2A3A54]" />
                <MdOutlineMenuBook className={baseIcon} />
                <span>{t("adminDash.nav.knowledgeBase")}</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="./translations"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  [baseItem, isActive ? activeItem : inactiveItem, "group"].join(" ")
                }
              >
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full opacity-0 group-[.active]:opacity-100 bg-gradient-to-b from-[#EE5F2B] to-[#2A3A54]" />
                <MdTranslate className={baseIcon} />
                <span>{t("adminDash.nav.translations")}</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="./my-account"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  [baseItem, isActive ? activeItem : inactiveItem, "group"].join(" ")
                }
              >
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full opacity-0 group-[.active]:opacity-100 bg-gradient-to-b from-[#EE5F2B] to-[#2A3A54]" />
                <PiUserCircleGearFill className={baseIcon} />
                <span>{t("adminDash.nav.settings")}</span>
              </NavLink>
            </li>
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
